import CheckIcon from "@mui/icons-material/CheckRounded";
import OpenInNewIcon from "@mui/icons-material/OpenInNewRounded";
import { Box, Button, IconButton, Link, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { useState, type PropsWithChildren } from "react";
import { useIntercomContext } from "@entrilia/shared/contexts/IntercomContext";
import LockIcon from "@entrilia/shared/icons/LockIcon";
import PromoImageActivity from "../../../../images/portal_promo_activity.webp";
import PromoImageDataroom from "../../../../images/portal_promo_dataroom.webp";
import PromoImageDocuments from "../../../../images/portal_promo_documents.webp";
import { intercomArticleIds } from "../../../../utilities/intercomArticles";

const LockedPortalPagePopover = ({ children }: PropsWithChildren) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);

  return (
    <>
      <Tooltip title="Click to view details" arrow>
        <IconButton ref={setButtonEl} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
          <LockIcon />
        </IconButton>
      </Tooltip>

      <Popover
        open={isPopoverOpen}
        onClose={() => setIsPopoverOpen(false)}
        anchorEl={buttonEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { p: 2, width: 332 } } }}
      >
        {children}
      </Popover>
    </>
  );
};

export const LockedInvestorPortalPopover = () => {
  return (
    <LockedPortalPagePopover>
      <Stack spacing={2}>
        <Typography variant="h6">Investor Portal</Typography>
        <Box
          component="img"
          src={PromoImageDocuments}
          alt="Investor Portal"
          sx={{ width: "100%", height: "auto", aspectRatio: "1920 / 984" }}
        />
        <Stack spacing={1.5}>
          <Typography>
            Give every LP a secure, branded portal to access reports, documents and capital activity — and manage their
            access from one place.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>
              Invite contacts to a self-service portal where they can view investor data, documents and cash flows on
              the go
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>
              Control access with granular role-based permissions, MFA-protected logins and impersonation for support
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>
              Monitor invitation, registration and engagement activity for every contact in one place
            </Typography>
          </Stack>
          <Typography>Available with the Investor Portal add-on.</Typography>
        </Stack>
        <Button
          color="primary"
          variant="contained"
          startIcon={<OpenInNewIcon />}
          component={Link}
          href="https://www.entrilia.com/product-investor-portal"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn More
        </Button>
      </Stack>
    </LockedPortalPagePopover>
  );
};

export const LockedFundraisingPopover = () => {
  const { showArticle } = useIntercomContext();

  return (
    <LockedPortalPagePopover>
      <Stack spacing={2}>
        <Typography variant="h6">Fundraising Data Room</Typography>
        <Box
          component="img"
          src={PromoImageDataroom}
          alt="Fundraising"
          sx={{ width: "100%", height: "auto", aspectRatio: "1920 / 1120" }}
        />
        <Stack spacing={1.5}>
          <Typography>
            Share fundraising materials with prospects and LPs through a secure, branded data room — and see exactly how
            they engage.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>
              Upload and share decks, documents and data sheets in a clean, mobile-optimized portal that matches your
              brand
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>Control access with granular permissions, watermarks and MFA-protected logins</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>Track page-by-page engagement to gauge investor interest in real time</Typography>
          </Stack>
          <Typography>Available with the Fundraising Data Room add-on.</Typography>
        </Stack>
        <Button
          color="primary"
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={() => showArticle(intercomArticleIds.FUNDRAISING)}
        >
          Learn More
        </Button>
      </Stack>
    </LockedPortalPagePopover>
  );
};

export const LockedDocumentActivityPopover = () => {
  const { showArticle } = useIntercomContext();

  return (
    <LockedPortalPagePopover>
      <Stack spacing={2}>
        <Typography variant="h6">Document Activity</Typography>
        <Box
          component="img"
          src={PromoImageActivity}
          alt="Document Activity"
          sx={{ width: "100%", height: "auto", aspectRatio: "1668 / 1386" }}
        />
        <Stack spacing={1.5}>
          <Typography>See exactly how investors engage with every document you publish through the portal.</Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>Track views, downloads and total time spent on each document by every recipient</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>Drill into page-by-page engagement to gauge investor attention and completion</Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <CheckIcon color="primary" />
            <Typography>
              Spot distribution gaps and follow up with contacts who haven&apos;t opened key materials
            </Typography>
          </Stack>
          <Typography>Available with the Investor Portal add-on.</Typography>
        </Stack>
        <Button
          color="primary"
          variant="contained"
          startIcon={<OpenInNewIcon />}
          onClick={() => showArticle(intercomArticleIds.DOCUMENT_ACTIVITY)}
        >
          Learn More
        </Button>
      </Stack>
    </LockedPortalPagePopover>
  );
};
